import { FormEvent, useEffect } from "react";
import { Head, Link, router, useForm } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface RegionOption {
  id: number;
  name: string;
  ancestry: string | null;
}

type RegionFormData = {
  name: string;
  sort: string;
  parent_id: string;
};

interface Props {
  region: Partial<Omit<RegionFormData, "parent_id"> & { parent_id?: number | null }>;
  all_regions: RegionOption[];
}

export default function Create({ region, all_regions }: Props) {
  const { data, setData, processing, errors } = useForm<RegionFormData>({
    name: region?.name || "",
    sort: region?.sort?.toString() || "",
    parent_id: region?.parent_id?.toString() || "",
  });

  useEffect(() => {
    if (region && Object.keys(errors).length > 0) {
      setData({
        name: region?.name || "",
        sort: region?.sort?.toString() || "",
        parent_id: region?.parent_id?.toString() || "",
      });
    }
  }, [region, errors, setData]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Ensure parent_id is always included in the request, even if empty
    const submitData = {
      region: {
        ...data,
        parent_id: data.parent_id || "",
      },
    };
    router.post("/desk/regions", submitData, {
      onError: () => {
        // Errors will be handled by Inertia
      },
    });
  };

  const renderFieldError = (field: keyof RegionFormData) => {
    const error = errors[field];
    if (!error) return null;
    return <p className="text-sm text-destructive">{error}</p>;
  };

  // Helper function to get display name with indentation for hierarchy
  const getRegionDisplayName = (regionOption: RegionOption): string => {
    if (!regionOption.ancestry) {
      return regionOption.name;
    }
    const depth = regionOption.ancestry.split("/").length;
    return "  ".repeat(depth) + regionOption.name;
  };

  return (
    <>
      <Head title="إضافة منطقة جديدة" />

      <Card>
        <CardHeader>
          <CardTitle>إضافة منطقة جديدة</CardTitle>
          <CardDescription>أدخل تفاصيل المنطقة لإضافتها إلى النظام</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">اسم المنطقة</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(event) => setData("name", event.target.value)}
                  placeholder="اسم المنطقة"
                  aria-invalid={errors.name ? "true" : "false"}
                />
                {renderFieldError("name")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="parent_id">المنطقة الأم (اختياري)</Label>
                <Select
                  value={data.parent_id || "none"}
                  onValueChange={(value) => setData("parent_id", value === "none" ? "" : value)}
                >
                  <SelectTrigger id="parent_id" aria-invalid={errors.parent_id ? "true" : "false"}>
                    <SelectValue placeholder="اختر المنطقة الأم (اتركه فارغاً للجذر)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">لا يوجد (منطقة جذر)</SelectItem>
                    {all_regions.map((regionOption) => (
                      <SelectItem key={regionOption.id} value={regionOption.id.toString()}>
                        {getRegionDisplayName(regionOption)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError("parent_id")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sort">الترتيب</Label>
                <Input
                  id="sort"
                  type="number"
                  value={data.sort}
                  onChange={(event) => setData("sort", event.target.value)}
                  placeholder="0"
                  min="0"
                  aria-invalid={errors.sort ? "true" : "false"}
                />
                {renderFieldError("sort")}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" asChild>
                <Link href="/desk/regions">إلغاء</Link>
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? "جاري الحفظ..." : "حفظ المنطقة"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
